import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { FlaskConical, Loader2 } from "lucide-react";
import { setUser } from "../../redux/authSlice";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // --- HELPER: Route Based on Role ---
  const handleLoginSuccess = (userData, token) => {
    localStorage.setItem("token", token);
    dispatch(setUser(userData));
    toast.success(`Welcome back, ${userData.user_name || 'User'}!`);
    
    // If they are an org admin, send them to the admin control panel
    if (userData.role === "org_admin") {
      navigate("/admin-dashboard");
    } else {
      // Everyone else goes to the unified projects page
      navigate("/projects");
    }
  };

  // --- MANUAL LOGIN ---
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) return toast.error("Please enter your email.");
    if (!password) return toast.error("Please enter your password.");

    try {
      setLoading(true);
      const response = await axios.post("https://api.testoraglobal.in/api/user/login", {
        email,
        password,
      });

      if (response.data.success && response.data.token) {
        handleLoginSuccess(response.data.user, response.data.token);
      } else if (response.data.success) {
        toast.error("Sign-in succeeded, but no authentication token was returned.");
      } else {
        toast.error(response.data.message || "Invalid credentials");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] relative overflow-hidden text-white px-4">
      {/* Background Glow */}
      <div className="absolute top-[-200px] left-[-200px] w-[500px] h-[500px] bg-purple-600 rounded-full blur-[150px] opacity-20"></div>
      <div className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] bg-pink-600 rounded-full blur-[150px] opacity-20"></div>

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md relative z-10">
        
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div whileHover={{ scale: 1.05 }} className="flex items-center justify-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <FlaskConical size={20} />
            </div>
            <h1 className="text-3xl font-bold tracking-wide">
              <span className="text-3xl font-bold text-white hover:text-purple-500 animate-pulse">TESTORA</span>
            </h1>
          </motion.div>
          <p className="text-gray-400 mt-1">AI-Powered Testing Platform</p>
        </div>

        {/* Card */}
        <motion.div className="bg-[#0B1220]/70 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 shadow-2xl shadow-purple-900/10">
          <h2 className="text-2xl font-semibold mb-6">Sign in to your account</h2>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Email address</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="you@example.com" 
                disabled={loading} 
                className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500 transition disabled:opacity-50" 
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Password</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="••••••••" 
                disabled={loading} 
                className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500 transition disabled:opacity-50" 
              />
            </div>

            <div className="flex justify-between items-center mb-6">
              <label className="flex items-center text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" className="mr-2 accent-purple-600" /> Remember me
              </label>
              <span onClick={() => navigate("/forgot-password")} className="text-purple-400 hover:text-purple-300 text-sm transition cursor-pointer">Forgot password?</span>
            </div>

            <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={loading} className="w-full py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 font-semibold shadow-lg shadow-purple-600/20 hover:shadow-purple-600/40 transition duration-300 disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center gap-2">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : "Sign in"}
            </motion.button>

          </form>

        </motion.div>
      </motion.div>
    </div>
  );
}